<template>
	<div id="resizible-wrapper" class="container detail-shell px-4 relative">
		<Spinner
			v-if="detailResolving || !highlightReady"
			text="Retrieving data..."
			classes="text-dark-8 mh-60"
			spinner-color="var(--c-dark-8)"
			spinner-size="15px"
		/>
		<main v-else-if="highlight" class="flex flex-col gap-4 pb-8">
			<PageHeader :title="highlightName" mobile-inline show-subtitle-on-mobile class="!mb-0">
				<template #leading>
					<BackButton :to="{ name: 'Highlight' }" title="Back to Live Clipping" />
				</template>
				<template #title>
					<!-- Inline rename: the invisible twin sizes the input to its content. -->
					<span class="highlight-name-wrap inline-grid max-w-full align-top">
						<span class="highlight-name-twin invisible col-start-1 row-start-1 whitespace-pre px-1.5" aria-hidden="true">{{ highlightName || ' ' }}</span>
						<input
							v-model="highlightName"
							type="text"
							autocomplete="off"
							autocorrect="off"
							autocapitalize="off"
							spellcheck="false"
							aria-label="Live Clipping name. Edit to rename."
							title="Click to rename"
							class="highlight-name col-start-1 row-start-1 w-full min-w-0 rounded-md border-0 bg-transparent px-1.5 py-0.5 hover:bg-surface-3/60 focus:bg-surface-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-main"
							@keydown.enter.prevent="$event.target.blur()"
							@change="onHighlightChange"
						>
					</span>
				</template>
				<template #subtitle>
					<span class="flex min-w-0 max-w-full items-center gap-1.5">
						<icon-base class="shrink-0 text-surface-8"><icon-link /></icon-base>
						<span class="min-w-0 truncate">{{ highlight.stream?.name }}</span>
					</span>
				</template>

				<span
					v-if="isDisabled"
					class="inline-flex h-8 items-center gap-2 rounded-full bg-surface-3 px-3 text-tiny font-medium text-surface-8"
				>
					Disabled
				</span>
				<!-- Reserved slot: invisible until the clipper knows the state, so the header never reflows -->
				<span
					v-else
					class="inline-flex h-8 min-w-[124px] items-center justify-center gap-2 rounded-full px-3 text-tiny font-medium ring-1"
					:class="[
						clipperStatus.known ? '' : 'invisible',
						clipperStatus.live ? 'bg-error/10 text-error-light ring-error/30' : 'bg-surface-3 text-surface-8 ring-surface-4',
					]"
					role="status"
				>
					<span class="h-2 w-2 shrink-0 rounded-full" :class="clipperStatus.live ? 'bg-error' : 'border border-surface-8'" aria-hidden="true" />
					{{ clipperStatus.live ? 'Stream live' : 'Stream offline' }}
				</span>
				<FormButton v-if="isDisabled" type="main" size="md" :is-loading="statusBusy" @click="setDisabled(false)">
					Enable
				</FormButton>
				<drop-down-menu type="secondary" size="md" is-icon position="right" aria-label="More actions" list-classes="min-w-[160px]">
					<template #toggle-button>
						<icon-base><icon-elipsis /></icon-base>
					</template>
					<template #menu-items>
						<drop-down-menu-item v-if="!isDisabled" @click="showModal('modal-highlight-disable')">
							Disable
						</drop-down-menu-item>
						<drop-down-menu-item v-else @click="setDisabled(false)">
							Enable
						</drop-down-menu-item>
						<drop-down-menu-item custom-classes="h-10 md:h-8 !p-3 md:!p-2 hover:bg-surface-3 text-error-light hover:text-error-light text-nowrap leading-none text-base md:text-tiny flex items-center w-full truncate" @click="showModal('modal-highlight-delete')">
							Delete
						</drop-down-menu-item>
					</template>
				</drop-down-menu>
			</PageHeader>

			<LiveClipper
				v-if="!isDisabled && dvrBaseUrl"
				:base-url="dvrBaseUrl"
				:max-duration="maxHighlightDurationSec"
				:stream-name="highlight.stream?.name || highlight.name"
				:highlight-id="highlight._id"
				:stream-id="highlight.stream?._id || ''"
				@status="onClipperStatus"
			/>

			<section v-else class="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_360px]">
				<div class="flex aspect-video w-full flex-col items-center justify-center gap-3 rounded-lg bg-black px-6 text-center">
					<p class="m-0 text-sm text-surface-text-2">Live Clipping is disabled for this stream.</p>
					<p class="m-0 max-w-sm text-tiny text-surface-8">Recording continues only while it is enabled. Enable it to clip and download moments from the live stream.</p>
					<FormButton type="main" size="md" :is-loading="statusBusy" @click="setDisabled(false)">Enable Live Clipping</FormButton>
				</div>
				<div class="hidden rounded-lg bg-surface-2 p-4 text-tiny text-surface-8 lg:block">
					Quick clips, In/Out editing and downloads appear here once Live Clipping is enabled.
				</div>
			</section>
		</main>

		<confirm-modal
			modal-id="modal-highlight-disable"
			title="Disable Live Clipping?"
			message="Recording stops while Live Clipping is disabled. Existing recordings stay available until they expire."
			ok-text="Disable"
			cancel-text="Cancel"
			modal-type="secondary"
			@modal-confirm="setDisabled(true)"
		/>
		<confirm-modal
			modal-id="modal-highlight-delete"
			title="Delete this Live Clipping?"
			message="This removes the Live Clipping setup for this stream. Clips you have already downloaded are not affected."
			ok-text="Delete"
			cancel-text="Cancel"
			modal-type="danger"
			@modal-confirm="highlightDelete"
		/>
	</div>
</template>

<script>
import _ from 'lodash';
import { mapGetters } from 'vuex';
import { showModal } from '@/Utils/modalBus';
import Spinner from '@/components/ui/Spinner.vue';
import HighlightService from '@/services/HighlightService';
import ConfirmModal from '@/components/modals/ConfirmModal.vue';
import PageHeader from '@/components/ui/PageHeader.vue';
import FormButton from '@/components/Atoms/FormButton.vue';
import DropDownMenu from '@/components/Atoms/DropDownMenu.vue';
import DropDownMenuItem from '@/components/Atoms/DropDownMenuItem.vue';
import IconBase from '@/components/icon/IconBase.vue';
import IconElipsis from '@/components/icon/IconElipsis.vue';
import BackButton from '@/components/Atoms/BackButton.vue';
import IconLink from '@/components/icon/IconLink.vue';
import LiveClipper from '@/components/LiveClipping/LiveClipper.vue';
import { buildDvrBase } from '@/components/LiveClipping/dvrUtils';
import { track, EVENTS } from '@/lib/tracking';

const DEFAULT_MAX_HIGHLIGHT_MINUTES = 15;

export default {
	name: 'StreamManageHighlights',
	components: {
		Spinner,
		ConfirmModal,
		PageHeader,
		FormButton,
		DropDownMenu,
		DropDownMenuItem,
		IconBase,
		IconElipsis,
		BackButton,
		IconLink,
		LiveClipper,
	},
	data() {
		return {
			highlightId: null,
			highlight: null,
			highlightName: '',
			isDisabled: true,
			statusBusy: false,
			detailResolving: true,
			detailSyncing: false,
			detailRequestToken: 0,
			scopeAlive: true,
			// Reported by LiveClipper from ranges liveness / media_info.
			clipperStatus: { known: false, live: false },
		};
	},
	computed: {
		...mapGetters({
			subscriptions: 'User/subscriptions',
		}),
		highlightReady() {
			return String(this.highlight?._id || '') === String(this.highlightId || this.$route.params.highlightId || '');
		},
		userId() {
			return this.highlight?.stream?.user || this.$store.state.User._id;
		},
		dvrBaseUrl() {
			const key = this.highlight?.stream?.key;
			if (!key || !this.userId) return '';
			return buildDvrBase(this.userId, key);
		},
		// Plan limit in minutes (subscription definition, with override).
		maxHighlightDurationMinutes() {
			const sub = (this.subscriptions || []).find((item) => item.enabled && item.category === 'live');
			const override = Number(sub?.definitionOverride?.maxHighlightDuration);
			if (override > 0) return override;
			const plan = Number(sub?.package?.definition?.maxHighlightDuration);
			if (plan > 0) return plan;
			return DEFAULT_MAX_HIGHLIGHT_MINUTES;
		},
		maxHighlightDurationSec() {
			return this.maxHighlightDurationMinutes * 60;
		},
	},
	mounted() {
		this.highlightId = this.$route.params.highlightId;
		void this.loadHighlight(this.highlightId);
	},
	beforeUnmount() {
		this.scopeAlive = false;
		this.detailRequestToken += 1;
	},
	watch: {
		'$route.params.highlightId'(id, previousId) {
			if (previousId !== undefined && String(id) !== String(previousId)) {
				this.highlightId = id;
				void this.loadHighlight(id);
			}
		},
	},
	methods: {
		showModal,
		applyHighlightData(highlight) {
			this.highlight = highlight;
			this.highlightName = highlight.name;
			this.isDisabled = !!highlight.isDisabled;
		},
		normalizeListedHighlight(listed) {
			const highlight = _.cloneDeep(listed);
			if (typeof highlight.stream === 'string') {
				const stream = (this.$store.getters['Streams/allInOneStreams'] || []).find(
					(item) => item._id === highlight.stream
				);
				if (stream) highlight.stream = stream;
			} else if (Array.isArray(highlight.streamobj) && highlight.streamobj[0]) {
				highlight.stream = highlight.streamobj[0];
			}
			return highlight;
		},
		async loadHighlight(highlightId) {
			if (!highlightId) return;

			const token = ++this.detailRequestToken;
			const isCurrentRequest = () => (
				this.scopeAlive
				&& token === this.detailRequestToken
				&& String(this.$route.params.highlightId) === String(highlightId)
			);
			let seedSnapshot = null;

			this.detailResolving = true;
			this.detailSyncing = true;
			this.clipperStatus = { known: false, live: false };

			const seedFromList = () => {
				if (!isCurrentRequest() || this.highlightReady) return false;
				const listed = (this.$store.getters['Highlight/rawHighlight'] || []).find(
					(item) => item._id === highlightId
				);
				if (!listed) return false;
				this.applyHighlightData(this.normalizeListedHighlight(listed));
				seedSnapshot = JSON.stringify(this.highlight);
				this.detailResolving = false;
				return true;
			};

			seedFromList();

			void this.$store.dispatch('User/loadUser').catch((err) => {
				if (isCurrentRequest()) console.error('Failed to load user', err);
			});
			void this.$store.dispatch('Streams/getStreams').then(() => {
				if (!isCurrentRequest()) return;
				seedFromList();
				if (this.highlight?.stream?.key) return;
				const listed = (this.$store.getters['Highlight/rawHighlight'] || []).find(
					(item) => item._id === highlightId
				);
				if (!listed) return;
				const seeded = this.normalizeListedHighlight(listed);
				if (!seeded.stream?.key) return;
				this.applyHighlightData(seeded);
				if (seedSnapshot !== null) {
					seedSnapshot = JSON.stringify(this.highlight);
				}
			}).catch((err) => {
				if (isCurrentRequest()) console.error('Failed to load streams', err);
			});
			void this.$store.dispatch('Highlight/getHighlight').then(() => seedFromList()).catch((err) => {
				if (isCurrentRequest()) console.error('Failed to synchronize Live Clipping list', err);
			});

			try {
				const freshHighlight = await HighlightService.getHighlightbyid(highlightId);
				if (!isCurrentRequest()) return;

				const seededStateUnchanged = seedSnapshot !== null
					&& JSON.stringify(this.highlight) === seedSnapshot;
				if (seedSnapshot === null || seededStateUnchanged) {
					this.applyHighlightData(freshHighlight);
				}
				this.detailResolving = false;
			} catch (err) {
				if (!isCurrentRequest()) return;
				if (!this.highlightReady) {
					this.$notify({
						group: 'error',
						title: err.error || 'Could not load this Live Clipping',
						text: err.message,
					});
					await this.$router.push({ name: 'Highlight' });
					return;
				}
				this.detailResolving = false;
				this.$notify({
					group: 'error',
					title: 'Live Clipping refresh failed',
					text: err.message,
				});
			} finally {
				if (isCurrentRequest()) this.detailSyncing = false;
			}
		},
		async setDisabled(disabled) {
			if (this.statusBusy) return;
			this.statusBusy = true;
			try {
				await HighlightService.checkDisabled(this.highlight._id, disabled);
				this.isDisabled = disabled;
				this.highlight.isDisabled = disabled;
				this.$notify({
					group: 'success',
					title: disabled ? 'Live Clipping disabled' : 'Live Clipping enabled',
				});
			} catch (err) {
				this.$notify({
					group: 'error',
					title: disabled ? 'Could not disable Live Clipping' : 'Could not enable Live Clipping',
					text: err.message,
				});
			} finally {
				this.statusBusy = false;
			}
		},
		async onHighlightChange() {
			const next = (this.highlightName || '').trim();
			if (!next) {
				this.highlightName = this.highlight.name;
				return;
			}
			if (this.highlight.name === next) return;
			try {
				await HighlightService.setHighlightName(this.highlight._id, next);
				this.highlight.name = next;
				this.highlightName = next;
			} catch (err) {
				this.highlightName = this.highlight.name;
				this.$notify({
					group: 'error',
					title: 'Could not rename this Live Clipping',
					text: err.message,
				});
			}
		},
		onClipperStatus({ known, live }) {
			this.clipperStatus = { known: !!known, live: !!live };
		},
		async highlightDelete() {
			try {
				const res = await HighlightService.deleteHighlight(this.highlight._id);
				if (res && res.success) {
					track(EVENTS.HIGHLIGHT_DELETED, { highlightId: this.highlight._id });
					this.$notify({ group: 'success', title: 'Live Clipping deleted' });
					this.$router.push({ name: 'Highlight' });
				}
			} catch (err) {
				this.$notify({
					group: 'error',
					title: 'Could not delete this Live Clipping',
					text: err.message,
				});
			}
		},
	},
};
</script>

<style scoped>
/* The rename input lives inside PageHeader's h1 and inherits its type. */
.highlight-name,
.highlight-name-twin {
	font: inherit;
	letter-spacing: inherit;
	line-height: inherit;
	color: inherit;
}

.highlight-name-wrap {
	min-width: 6ch;
	max-width: min(100%, 48rem);
}
</style>
